import { createContext, useContext, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Outlet } from "react-router-dom";
import { PageHeader, TabBar } from "@fox1ck-org/ui";
import { RuleDomain } from "@gen/tracker/v1/rule_pb";

import { useT, type TKey } from "@/lib/i18n";
import { useRouteTab } from "@/lib/route-tab";
import { useListGoals } from "@/lib/services/goal-service";
import { useListRules } from "@/lib/services/rule-service";

const ActionsSlot = createContext<HTMLElement | null>(null);

/** A tab's own buttons, drawn at the right of the «Настройки» title (доски SettingsGoals, SettingsAlerts). */
export function SettingsActions({ children }: { children: ReactNode }) {
  const target = useContext(ActionsSlot);
  return target ? createPortal(children, target) : null;
}

const TABS = ["workspace", "goals", "connections", "alerts", "history"] as const;
type Tab = (typeof TABS)[number];
const LABEL: Record<Tab, TKey> = {
  workspace: "settings.tab.workspace",
  goals: "settings.tab.goals",
  connections: "settings.tab.connections",
  alerts: "settings.tab.alerts",
  history: "settings.tab.history",
};

/**
 * «Настройки» (доски Settings, SettingsGoals, SettingsAlerts): one title,
 * the tabs as addresses (/settings, /settings/goals, … — decision 04), the
 * goals and the alerts counted by their own lists, and the tab below. Each
 * tab draws only its body; its buttons go into the title row.
 */
export function SettingsSection() {
  const t = useT();
  const { tab, setTab } = useRouteTab("/settings", TABS, "workspace");
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const goals = useListGoals(false);
  const alerts = useListRules({ domain: RuleDomain.METRICS });
  const count: Partial<Record<Tab, number | undefined>> = { goals: goals.data?.length, alerts: alerts.data?.total };
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 px-5 pt-4">
        <PageHeader
          title={t("nav.settings")}
          actions={
            <div ref={setSlot} className="flex items-center gap-3">
              {tab === "workspace" ? <span className="text-xs text-muted-foreground">{t("ops.set.instant")}</span> : null}
            </div>
          }
          tabs={
            <TabBar
              aria-label={t("nav.settings")}
              value={tab}
              onValueChange={(v) => setTab(v as Tab)}
              tabs={TABS.map((k) => ({ value: k, label: t(LABEL[k]), count: count[k], panelId: "settings-tab" }))}
            />
          }
        />
      </div>
      <ActionsSlot.Provider value={slot}>
        <div id="settings-tab" role="tabpanel" className="flex min-h-0 flex-1 flex-col">
          <Outlet />
        </div>
      </ActionsSlot.Provider>
    </div>
  );
}
